/**
 * The current project, at the top of the sidebar; a click lists your projects
 * and "New project", like the workspace switcher in Linear or Langfuse. Any
 * switch is a full page load (`/p/<slug>`), so nothing cached from one project
 * shows in another. Names only: roles live in Settings → Members. Borderless,
 * a header rather than a form field: the project's initials, name and chevron.
 */

import { useRef, useState } from "react";
import { ChevronsUpDown } from "lucide-react";
import { initialsOf } from "@/features/auth/permissions";
import { useProject, useProjects } from "./hooks";
import { NewProjectDialog } from "./new-project-dialog";
import { ProjectMenu } from "./project-menu";

export function ProjectSwitcher({ compact = false }: { compact?: boolean }) {
  const { project } = useProject();
  const [open, setOpen] = useState(false);
  // Hover or focus starts loading the list, so it is usually there by the click.
  const [warm, setWarm] = useState(false);
  const [creating, setCreating] = useState(false);
  const projects = useProjects(open || warm);
  const button = useRef<HTMLButtonElement>(null);
  if (!project) return null;
  const close = (refocus = true) => {
    setOpen(false);
    if (refocus) button.current?.focus();
  };

  return (
    <>
      <button
        ref={button}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Project: ${project.name}`}
        title={project.name}
        onClick={() => setOpen(!open)}
        onMouseEnter={() => setWarm(true)}
        onFocus={() => setWarm(true)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown" && !open) (e.preventDefault(), setOpen(true));
        }}
        className={`flex h-[34px] w-full min-w-0 items-center gap-2 rounded-[7px] text-sm font-semibold hover:bg-nav-hover aria-expanded:bg-nav-hover ${
          compact ? "justify-center px-0" : "px-1.5"
        }`}
      >
        <span
          aria-hidden
          className="flex size-6 shrink-0 items-center justify-center rounded-md bg-primary text-[10px] font-semibold text-on-primary"
        >
          {initialsOf(project.name, project.slug)}
        </span>
        {!compact && (
          <>
            <span className="min-w-0 flex-1 truncate text-left">{project.name}</span>
            <ChevronsUpDown size={14} className="shrink-0 text-faint" aria-hidden />
          </>
        )}
      </button>
      {open && (
        <ProjectMenu
          anchor={button}
          current={project.slug}
          projects={projects}
          onClose={close}
          onCreate={() => (close(), setCreating(true))}
        />
      )}
      {creating && <NewProjectDialog onClose={() => setCreating(false)} />}
    </>
  );
}
